import { omit, pick } from 'lodash-es'
import { treq } from '#app/lib/treq'
import { arrayIncludes, objectEntries, objectFromEntries, objectKeys } from '#app/lib/utils'
import { allowedValuesPerTypePerProperty } from '#entities/components/editor/lib/suggestions/property_values_shortlist'
import { pluralize } from '#entities/lib/types/entities_types'
import type { PropertiesMetadata } from '#server/controllers/data/properties_metadata'
import type { PropertyConfig } from '#server/controllers/entities/lib/properties/properties'
import type { PropertyCategory } from '#server/controllers/entities/lib/properties/properties'
import type { EntityType, ExtendedEntityType, InvPropertyUri, PropertyUri } from '#server/types/entity'

export interface PropertyCategoryClientConfig {
  label?: string
  startShown: boolean
  customizablePropertyList: boolean
}

export const propertiesCategories = {
  general: {
    startShown: true,
    customizablePropertyList: false,
  },
  'author-roles': {
    label: 'Author roles',
    startShown: true,
    customizablePropertyList: false,
  },
  'work-relations': {
    label: 'Relations with other works',
    startShown: true,
    customizablePropertyList: false,
  },
  'web-presence': {
    label: 'Presence on the web',
    startShown: false,
    customizablePropertyList: true,
  },
  'bibliographic-databases': {
    label: 'Bibliographic databases',
    startShown: false,
    customizablePropertyList: true,
  },
  misc: {
    label: 'Miscellaneous',
    startShown: false,
    customizablePropertyList: false,
  },
} satisfies Record<PropertyCategory, PropertyCategoryClientConfig>

const propertiesRes = await treq('data', 'get', 'properties')
export const properties: PropertiesMetadata = propertiesRes.properties

function getPropertyCategory (property: PropertyUri) {
  return properties[property].category || 'general'
}

export interface CustomPropertyConfig extends PropertyConfig {
  customLabel?: string
}

export type CustomPropertyConfigByProperty = Record<PropertyUri, CustomPropertyConfig>

export const propertiesPerType: Partial<Record<EntityType, CustomPropertyConfigByProperty>> = {}

export const propertiesPerCategory: Partial<Record<PropertyCategory, PropertyUri[]>> = objectFromEntries(objectKeys(propertiesCategories).map(category => [ category, [] ]))

const hiddenProperties = [
  'invp:P1',
  'invp:P3',
] satisfies InvPropertyUri[]

for (const [ property, propertyMetadata ] of objectEntries(properties)) {
  if (!arrayIncludes(hiddenProperties, property)) {
    const { subjectTypes } = propertyMetadata
    const category = getPropertyCategory(property)
    const allowedValuesShortlist = allowedValuesPerTypePerProperty[property]
    for (const type of subjectTypes) {
      const propertyCanBeEdited = !allowedValuesShortlist || allowedValuesShortlist[pluralize(type)].length > 1
      if (propertyCanBeEdited) {
        propertiesPerType[type] ??= {}
        propertiesPerType[type][property] = propertyMetadata
        propertiesPerCategory[category].push(property)
      }
    }
  }
}

export const priorityPropertiesPerType = {
  human: [ 'wdt:P1412' ],
  work: [ 'wdt:P31', 'wdt:P50', 'wdt:P577' ],
  serie: [ 'wdt:P31', 'wdt:P50' ],
  edition: [ 'wdt:P629', 'wdt:P437', 'wdt:P1476', 'wdt:P1680', 'wdt:P123', 'invp:P2', 'wdt:P407', 'wdt:P577' ],
  publisher: [ 'wdt:P856', 'wdt:P112', 'wdt:P571', 'wdt:P576' ],
  collection: [ 'wdt:P1476', 'wdt:P407', 'wdt:P123', 'wdt:P856' ],
  article: [],
} satisfies Partial<Record<ExtendedEntityType, PropertyUri[]>>

const workAndSerieCustomLabels = {
  'wdt:P31': 'Type',
  'wdt:P58': 'scenarist',
  'wdt:P98': 'editorial director',
  'wdt:P407': 'original language',
  'wdt:P577': 'first publication date',
} satisfies Partial<Record<PropertyUri, string>>

const customLabels = {
  human: {},
  work: workAndSerieCustomLabels,
  serie: workAndSerieCustomLabels,
  edition: {
    'wdt:P110': 'edition illustrator',
    'wdt:P1476': 'edition title',
    'wdt:P1680': 'edition subtitle',
    'wdt:P629': 'work from which this is an edition',
    'wdt:P407': 'edition language',
    'wdt:P2635': 'number of volumes',
  },
  publisher: {
    'wdt:P571': 'date of foundation',
    'wdt:P576': 'date of dissolution',
  },
  collection: {
    'wdt:P1476': 'collection title',
  },
  article: {},
} satisfies Partial<Record<ExtendedEntityType, Record<PropertyUri, string>>>

for (const type of objectKeys<typeof propertiesPerType>(propertiesPerType)) {
  const typePriorityProperties = priorityPropertiesPerType[type]
  propertiesPerType[type] = {
    ...pick(propertiesPerType[type], typePriorityProperties),
    ...omit(propertiesPerType[type], typePriorityProperties),
  }
  const typeCustomLabels = customLabels[type]
  for (const [ property, customLabel ] of objectEntries(typeCustomLabels)) {
    propertiesPerType[type][property] = Object.assign({ customLabel }, propertiesPerType[type][property])
  }
}

export const locallyCreatableEntitiesTypes = [
  'human',
  'work',
  'serie',
  'publisher',
  'collection',
  'edition',
] satisfies EntityType[]

export const requiredPropertiesPerType = {
  edition: [ 'wdt:P629', 'wdt:P1476', 'wdt:P407' ] as readonly PropertyUri[],
  collection: [ 'wdt:P1476', 'wdt:P123' ] as readonly PropertyUri[],
}

const categoryPerProperty: Record<PropertyUri, PropertyCategory> = {}

for (const [ key, categoryData ] of objectEntries(propertiesPerCategory)) {
  for (const property of categoryData) {
    categoryPerProperty[property] = key
  }
}

export type PropertiesPerCategory = Record<PropertyCategory, CustomPropertyConfigByProperty>
export type PropertiesPerTypeAndCategory = Record<EntityType, PropertiesPerCategory>
// @ts-expect-error
export const propertiesPerTypeAndCategory: PropertiesPerTypeAndCategory = {}

for (const [ type, propertiesData ] of objectEntries(propertiesPerType)) {
  // Initialize propertiesPerTypeAndCategory[type][category] ahead to preserve category order
  propertiesPerTypeAndCategory[type] = objectFromEntries(objectKeys(propertiesCategories).map(category => [ category, {} ]))
  for (const [ property, propertyData ] of objectEntries(propertiesData)) {
    const category = categoryPerProperty[property]
    propertiesPerTypeAndCategory[type][category][property] = propertyData
  }
}

export function getPropertyDatatype (property: PropertyUri) {
  return properties[property]?.datatype
}
